export const LOCALES = ["ko", "ja", "id", "zh", "th", "tl", "ms", "vi", "es", "pt"] as const;
export type Locale = (typeof LOCALES)[number];

// German and French currently have dedicated pricing pages only. Keep them
// separate from LOCALES until the rest of the public site is translated, so
// the global locale router never advertises routes that do not exist.
export const PRICING_ONLY_LOCALES = ["de", "fr"] as const;
export type PricingOnlyLocale = (typeof PRICING_ONLY_LOCALES)[number];

export const PRICING_ONLY_LOCALE_LABELS: Record<PricingOnlyLocale, string> = {
  de: "Deutsch",
  fr: "Français",
};

export const PRICING_ONLY_LOCALE_FLAGS: Record<PricingOnlyLocale, string> = {
  de: "🇩🇪",
  fr: "🇫🇷",
};

export const PRICING_ONLY_LOCALE_HREFLANG: Record<PricingOnlyLocale, string> = {
  de: "de",
  fr: "fr",
};

export const LOCALE_LABELS: Record<Locale, string> = {
  ko: "한국어",
  ja: "日本語",
  id: "Bahasa Indonesia",
  zh: "简体中文",
  th: "ภาษาไทย",
  tl: "Filipino",
  ms: "Bahasa Melayu",
  vi: "Tiếng Việt",
  es: "Español",
  pt: "Português",
};

export const LOCALE_FLAGS: Record<Locale, string> = {
  ko: "🇰🇷",
  ja: "🇯🇵",
  id: "🇮🇩",
  zh: "🇨🇳",
  th: "🇹🇭",
  tl: "🇵🇭",
  ms: "🇲🇾",
  vi: "🇻🇳",
  es: "🇪🇸",
  pt: "🇧🇷",
};

export const EN_FLAG = "🇺🇸";

// BCP-47 language tags for hreflang, html lang, JSON-LD inLanguage
export const LOCALE_HREFLANG: Record<Locale, string> = {
  ko: "ko",
  ja: "ja",
  id: "id",
  zh: "zh-CN",
  th: "th",
  tl: "tl",
  ms: "ms",
  vi: "vi",
  es: "es",
  pt: "pt-BR",
};

export const LOCALE_OG: Record<Locale, string> = {
  ko: "ko_KR",
  ja: "ja_JP",
  id: "id_ID",
  zh: "zh_CN",
  th: "th_TH",
  tl: "tl_PH",
  ms: "ms_MY",
  vi: "vi_VN",
  es: "es_ES",
  pt: "pt_BR",
};

export function hasLocale(value: string): value is Locale {
  return (LOCALES as readonly string[]).includes(value);
}

export function hasPricingOnlyLocale(value: string): value is PricingOnlyLocale {
  return (PRICING_ONLY_LOCALES as readonly string[]).includes(value);
}

export const SITE_URL = "https://audio2guitar.com";

// Paths that have direct locale equivalents under app/[locale]/*. All other
// paths (blog, niche landings, app, legal) fall back to the locale homepage
// when a user switches languages, to avoid 404s on yet-untranslated content.
export const LOCALIZED_PATHS = new Set<string>([
  "/",
  "/glossary",
  "/pricing",
  "/faq",
  "/suno",
  "/worship",
  "/wedding",
  "/teachers",
  "/songsterr-alternative",
  "/ultimate-guitar-alternative",
  "/guitar2tabs-alternative",
  "/chordify-alternative",
  "/blog/best-ai-guitar-tab-generators",
  "/blog/suno-to-guitar-tabs",
  "/blog/audio-to-guitar-tabs-guide",
  "/blog/how-to-read-guitar-tabs",
]);

export function localizedHref(locale: Locale, currentPath: string): string {
  if (LOCALIZED_PATHS.has(currentPath)) {
    return `/${locale}${currentPath === "/" ? "" : currentPath}`;
  }
  return `/${locale}`;
}

// Persistent locale preference. Set when the user clicks the banner suggestion
// or picks a language in the switcher. We persist to BOTH localStorage and
// a cookie so the server can read the preference on the first request,
// avoiding the English flash on /app/* and /login. Cookie is the source of
// truth for server reads; localStorage mirrors it for fast client reads.
export const PREFERRED_LOCALE_KEY = "audio2guitar.preferred-locale";
export const LOCALE_COOKIE_NAME = "a2g_locale";
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const prefix = `${name}=`;
  for (const part of document.cookie.split(";")) {
    const c = part.trim();
    if (c.startsWith(prefix)) return decodeURIComponent(c.slice(prefix.length));
  }
  return null;
}

function writeCookie(name: string, value: string | null): void {
  if (typeof document === "undefined") return;
  const attrs = `path=/; max-age=${value === null ? 0 : ONE_YEAR_SECONDS}; samesite=lax`;
  document.cookie = `${name}=${value === null ? "" : encodeURIComponent(value)}; ${attrs}`;
}

export function readPreferredLocale(): Locale | null {
  if (typeof window === "undefined") return null;
  try {
    const ls = window.localStorage.getItem(PREFERRED_LOCALE_KEY);
    if (ls && hasLocale(ls)) return ls;
  } catch {
    // localStorage unavailable (private mode, embedded contexts). Non-fatal.
  }
  const cookie = readCookie(LOCALE_COOKIE_NAME);
  if (cookie && hasLocale(cookie)) return cookie;
  return null;
}

export function writePreferredLocale(locale: Locale | null): void {
  if (typeof window === "undefined") return;
  try {
    if (locale === null) {
      window.localStorage.removeItem(PREFERRED_LOCALE_KEY);
    } else {
      window.localStorage.setItem(PREFERRED_LOCALE_KEY, locale);
    }
  } catch {}
  writeCookie(LOCALE_COOKIE_NAME, locale);
}

export function buildAlternates(path: string) {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  const enUrl = cleanPath === "/" ? SITE_URL : `${SITE_URL}${cleanPath}`;
  const languages: Record<string, string> = { en: enUrl };
  for (const locale of LOCALES) {
    const hreflang = LOCALE_HREFLANG[locale];
    languages[hreflang] = `${SITE_URL}/${locale}${cleanPath === "/" ? "" : cleanPath}`;
  }
  languages["x-default"] = enUrl;
  return languages;
}

export function buildPricingAlternates() {
  const languages = buildAlternates("/pricing");
  for (const locale of PRICING_ONLY_LOCALES) {
    languages[PRICING_ONLY_LOCALE_HREFLANG[locale]] = `${SITE_URL}/${locale}/pricing`;
  }
  return languages;
}
